// Standard Journey configuration model for Studio.
// DRAFT ONLY: the national neonatal clinical source has not been approved.
// The patient route is gated separately and this object is NOT a publication decision.
import { NEONATAL_PARENT_STAGES, NEONATAL_PARENT_CONTENT } from './neonatalParentJourney.js';
import { NEONATAL_PARENT_MODULES, NEONATAL_WORDS, NEONATAL_ROLE_SUMMARIES, NEONATAL_DRAFT_DISCLAIMER } from './neonatalParentEducationDraft.js';
import { neonatalSourceRevision } from './neonatalParentMachineDrafts.js';

export const NEONATAL_STANDARD_JOURNEY_ID = 'neonatal-care';
export const NEONATAL_NATIONAL_REVIEW_STATUS = 'pending_national_neonatal_clinical_review';
// Separate, fail-closed Studio catalogue entry: do not mutate the frozen 25-master registry.
export const NEONATAL_STANDARD_REVIEW_TEMPLATE = Object.freeze({
  id: NEONATAL_STANDARD_JOURNEY_ID,
  category: 'core',
  title: 'Your Baby’s Neonatal Care',
  summary: 'A flexible neonatal family information guide, prepared for clinical and Trust-specific review.',
  stages: Object.freeze(NEONATAL_PARENT_STAGES.map(({id, title}) => ({id, title}))),
  clinicalReleaseHold: true,
  nationalClinicalReviewStatus: NEONATAL_NATIONAL_REVIEW_STATUS,
  route: '/journeys/neonatal-care',
  trustLocalisation: Object.freeze({
    branding: 'overlay', links: 'placeholder', wording: 'overlay',
    roles: 'overlay', serviceDetails: 'overlay',
  }),
});

const roles = NEONATAL_PARENT_CONTENT.roles.map(role => ({ ...role }));
const links = NEONATAL_PARENT_CONTENT.usefulLinks.map(link => ({ ...link, url: null }));
const modulesById = Object.fromEntries(NEONATAL_PARENT_MODULES.map(module => [module.id, module]));
const summariesByRole = Object.fromEntries(NEONATAL_ROLE_SUMMARIES.map(role => [role.id, role]));

export function createNeonatalStandardJourney() {
  return {
    id: 'generic-neonatal-care',
    templateId: NEONATAL_STANDARD_JOURNEY_ID,
    accessKey: 'generic-neonatal-care',
    status: 'draft',
    public: false,
    clinicalReleaseHold: true,
    nationalClinicalReviewStatus: NEONATAL_NATIONAL_REVIEW_STATUS,
    nationalSourceRevision: neonatalSourceRevision(),
    trustId: 'generic_nhs',
    trustName: 'Your Trust',
    hospitalName: 'Your hospital or neonatal service',
    logoAlt: 'Your Trust logo here',
    logoPlaceholder: true,
    title: 'Your Baby’s Neonatal Care',
    eyebrow: 'Hospital Journey — clinical review draft',
    introduction: 'A flexible guide for parents and carers whose baby is receiving neonatal special, high-dependency or intensive care. These are information topics, not milestones.',
    category: 'core',
    completion: 'You can explore any section at any time and return when you want to.',
    disclaimer: NEONATAL_DRAFT_DISCLAIMER,
    roles: roles.map(role => ({ ...role })),
    questions: [...NEONATAL_PARENT_CONTENT.questions],
    usefulLinks: links.map(link => ({ ...link })),
    phraseKeys: [],
    stages: NEONATAL_PARENT_STAGES.map(stage => ({
      ...stage,
      explanation: { ...stage.explanation },
      roleIds: [...stage.roleIds],
      linkIds: [...stage.linkIds],
      questionIndexes: [...stage.questionIndexes],
      topics: modulesById[stage.id]?.topics?.map(topic => ({
        id: topic.id, title: topic.title, paragraphs: [...topic.paragraphs],
      })) || [],
      topicIntroduction: modulesById[stage.id]?.intro || '',
    })),
    neonatalGlossary: NEONATAL_WORDS.map(word => ({ ...word })),
    neonatalRoleSummaries: Object.values(summariesByRole).map(role => ({ ...role })),
    editorialOnly: true,
  };
}
